import React from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import AssignmentIcon from '@mui/icons-material/Assignment';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HistoryIcon from '@mui/icons-material/History';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BadgeIcon from '@mui/icons-material/Badge';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';
import { apiService } from '../../../services/api';
import UnifiedModal from '../../../components/portal/common/UnifiedModal';
import Navigation from '../../../components/portal/Navigation';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const PageWrapper = styled.div`
  min-height: 100vh;
  background: var(--page-bg, linear-gradient(135deg, var(--primary-orange) 0%, var(--primary-blue) 100%));
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 3;
  max-width: 1200px;
  margin: 0 auto;
  padding: var(--desktop-page-padding-x, 2rem);
  padding-top: 6rem;

  @media (max-width: 768px) {
    padding: 1rem var(--mobile-page-padding-x);
    padding-top: 5rem;
  }
`;

const ContentCard = styled(motion.div)`
  background: var(--card-bg, rgba(255, 255, 255, 0.95));
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid var(--card-border, rgba(255, 255, 255, 0.2));
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 2rem;
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;

  .title-group {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    h3 { 
      margin: 0; 
      font-size: 1.75rem; 
      font-weight: 900; 
      color: #222; 
      @media (max-width: 768px) {
        font-size: 1.25rem;
      }
    }
    svg { 
      color: var(--primary-blue); 
      font-size: 2rem; 
      @media (max-width: 768px) {
        font-size: 1.5rem;
      }
    }
  }

  .subtitle { 
    margin: 0.25rem 0 0 2.75rem; 
    color: #666; 
    font-weight: 500; 
    @media (max-width: 768px) {
      margin-left: 0;
      width: 100%;
      text-align: center;
    }
  }

  @media (max-width: 768px) {
    justify-content: center;
    .title-group {
      justify-content: center;
      width: 100%;
    }
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const PrimaryButton = styled(motion.button)<{ variant?: 'success' | 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.2rem;
  border-radius: 12px;
  font-weight: 800;
  color: #fff;
  border: none;
  cursor: pointer;
  background: ${p => {
    if (p.variant === 'success') return 'linear-gradient(135deg, #11998e, #38ef7d)';
    if (p.variant === 'secondary') return 'rgba(255, 255, 255, 0.2)';
    return 'linear-gradient(135deg, #667eea, #764ba2)';
  }};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const ExamList = styled.div`
  display: grid;
  gap: 1rem;
`;

const ExamItemContainer = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 12px;
  border: 1px solid rgba(102, 126, 234, 0.1);
  background: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 0.25rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.06);
    border-color: rgba(102, 126, 234, 0.3);
  }

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const ExamMainInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
  min-width: 0;

  .icon-wrapper {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: rgba(102, 126, 234, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--primary-blue);
    flex-shrink: 0;
  }

  .text-content {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    min-width: 0;
  }

  .exam-title {
    font-size: 0.95rem;
    font-weight: 800;
    color: #222;
    display: flex;
    align-items: center;
    gap: 0.4rem;
  }

  .exam-meta {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
    font-size: 0.8rem;
    color: #666;
  }

  .meta-item {
    display: flex;
    align-items: center;
    gap: 0.35rem;
  }
`;

const StatusChip = styled.span<{ status: string }>`
  padding: 0.35rem 0.8rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 800;
  color: #fff;
  text-transform: uppercase;
  background: ${p => {
    switch (p.status) {
      case 'PASSED': return 'linear-gradient(135deg, #11998e, #38ef7d)';
      case 'FAILED': return 'linear-gradient(135deg, #fc4a1a, #f7b733)';
      case 'UPCOMING': return 'linear-gradient(135deg, #667eea, #764ba2)';
      default: return 'linear-gradient(135deg, #6b7280, #9ca3af)';
    }
  }};
`;

const SectionTitle = styled.h4`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 1.1rem;
  font-weight: 800;
  color: #222;
  margin: 2rem 0 1.25rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid rgba(102, 126, 234, 0.1);
  
  &:first-of-type {
    margin-top: 0;
  }
`;

const ModalField = styled.div`
  display: grid;
  gap: 0.4rem;
  margin-bottom: 1.25rem;

  label {
    font-size: 0.75rem;
    font-weight: 800;
    color: #888;
    text-transform: uppercase;
  }

  input, select {
    padding: 0.75rem;
    border-radius: 10px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    font-family: inherit;
    font-size: 1rem;
    outline: none;
    transition: border-color 0.2s;

    &:focus {
      border-color: var(--primary-blue);
    }
  }
`;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1 }
};

type CreateExamModalProps = { open: boolean; onClose: () => void; onCreated: () => void; students: {id: string, name: string}[] };
const CreateExamModal: React.FC<CreateExamModalProps> = ({ open, onClose, onCreated, students }) => {
  const { t } = useTranslation();
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState<string|null>(null);
  const [examDate, setExamDate] = React.useState<string>('');
  const [examPlace, setExamPlace] = React.useState<string>('');
  const [studentId, setStudentId] = React.useState<string>('');
  const [licenseType, setLicenseType] = React.useState<string>('');

  const canSubmit = !!examDate && !saving;

  const handleCreate = async () => {
    if (!canSubmit) return;
    try {
      setSaving(true); setErr(null);
      const qs = new URLSearchParams();
      if (studentId.trim()) qs.set('studentId', studentId.trim());
      qs.set('examDate', examDate);
      if (examPlace.trim()) qs.set('examPlace', examPlace.trim());
      if (licenseType.trim()) qs.set('licenseType', licenseType.trim());
      await apiService.post<any>(API_ENDPOINTS.EXAMS.TEACHER_SLOTS + `?${qs.toString()}`);
      onCreated();
      onClose();
    } catch (e:any) {
      setErr(e?.message || (t('portal.exams.error_create', 'Failed to create exam') as string));
    } finally { setSaving(false); }
  };

  return (
    <UnifiedModal
      open={open}
      onClose={onClose}
      variant="center"
      title={t('portal.exams.schedule_new', 'Schedule New Exam') as string}
      size="sm"
      footer={(
        <>
          <PrimaryButton onClick={onClose} variant="secondary" style={{ color: '#444', background: '#f1f3f4' }}>{t('portal.common.cancel', 'Cancel')}</PrimaryButton>
          <PrimaryButton disabled={!canSubmit} onClick={handleCreate} variant="success">
            {saving ? (t('portal.exams.creating', 'Creating…') as string) : (t('portal.exams.create', 'Create Exam') as string)}
          </PrimaryButton>
        </>
      )}
    >
      <AnimatePresence>
        {err && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            style={{ color: '#fff', background: 'linear-gradient(135deg, #fc4a1a, #f7b733)', padding: '0.75rem', borderRadius: 10, marginBottom: '1rem', fontWeight: 700, fontSize: '0.9rem' }}
          >
            ⚠️ {err}
          </motion.div>
        )}
      </AnimatePresence>
      <div style={{ display:'grid' }}>
        <ModalField>
          <label>{t('portal.exams.date_time', 'Date & Time')}</label>
          <input type="datetime-local" value={examDate} onChange={e=> setExamDate(e.target.value)} />
        </ModalField>
        <ModalField>
          <label>{t('portal.exams.location', 'Location / Place')}</label>
          <input value={examPlace} onChange={e=> setExamPlace(e.target.value)} placeholder={t('portal.exams.location_placeholder', 'e.g. CBR Amsterdam') as string} />
        </ModalField>
        <ModalField>
          <label>{t('portal.register.license_type', 'License Type')}</label>
          <select value={licenseType} onChange={e=> setLicenseType(e.target.value)}>
            <option value="">— {t('portal.register.select_license', 'Select license type')} —</option>
            <option value="AM">{t('portal.register.license_am')}</option>
            <option value="A1">{t('portal.register.license_a1')}</option>
            <option value="A2">{t('portal.register.license_a2')}</option>
            <option value="A">{t('portal.register.license_a')}</option>
            <option value="B_MANUAL">{t('portal.register.license_b_manual')}</option>
            <option value="B_AUTOMATIC">{t('portal.register.license_b_automatic')}</option>
            <option value="BE">{t('portal.register.license_be')}</option>
            <option value="B_PLUS">{t('portal.register.license_b_plus')}</option>
            <option value="C1">{t('portal.register.license_c1')}</option>
            <option value="C1E">{t('portal.register.license_c1e')}</option>
            <option value="C">{t('portal.register.license_c')}</option>
            <option value="CE">{t('portal.register.license_ce')}</option>
            <option value="D1">{t('portal.register.license_d1')}</option>
            <option value="D1E">{t('portal.register.license_d1e')}</option>
            <option value="D">{t('portal.register.license_d')}</option>
            <option value="DE">{t('portal.register.license_de')}</option>
            <option value="T">{t('portal.register.license_t')}</option>
          </select>
        </ModalField>
        <ModalField>
          <label>{t('portal.exams.assign_student', 'Assign to Student')}</label>
          <select value={studentId} onChange={e=> setStudentId(e.target.value)}>
            <option value="">{t('portal.exams.select_student_optional', 'Select a student (optional)')}</option>
            {students.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </ModalField>
      </div>
    </UnifiedModal>
  );
};

const TeacherExams: React.FC = () => {
  const [showCreate, setShowCreate] = React.useState(false);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string|null>(null);
  const [exams, setExams] = React.useState<any[]>([]);
  const [studentsList, setStudentsList] = React.useState<{id: string, name: string}[]>([]);
  const [studentsMap, setStudentsMap] = React.useState<Record<string,string>>({});

  const formatDate = React.useCallback((iso?: string) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString(i18n.language || 'en-GB', { 
        year: 'numeric', 
        month: 'short', 
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Europe/Amsterdam' 
      });
    } catch {
      return new Date(iso).toLocaleDateString(i18n.language || 'en-GB', { year: 'numeric', month: 'short', day: '2-digit' });
    }
  }, [i18n.language]);

  const load = React.useCallback(async ()=>{
    setLoading(true); setError(null);
    try {
      const [exResp, stResp] = await Promise.all([
        apiService.get<any>(API_ENDPOINTS.EXAMS.TEACHER_ALL),
        apiService.get<any>(API_ENDPOINTS.TEACHER.STUDENTS),
      ]);
      const list = Array.isArray((exResp as any)?.data) ? (exResp as any).data : (Array.isArray(exResp) ? exResp : []);
      setExams(list);
      const studs = Array.isArray((stResp as any)?.data) ? (stResp as any).data : (Array.isArray(stResp) ? stResp : []);
      const map: Record<string,string> = {};
      const listForSelect: {id: string, name: string}[] = [];
      studs.forEach((s:any)=>{ 
        const name = `${s.firstName || ''} ${s.lastName || ''}`.trim() || s.username || s.email || `Student ${s.id}`;
        map[String(s.id)] = name;
        listForSelect.push({ id: String(s.id), name });
      });
      setStudentsMap(map);
      setStudentsList(listForSelect);
    } catch(e:any) {
      setError(e?.message || (t('portal.exams.error_load', 'Failed to load exams') as string));
    } finally { setLoading(false); }
  },[t]);

  React.useEffect(()=>{ load(); }, [load]);

  const computeStatus = (e: any): 'UPCOMING'|'COMPLETED'|'PASSED'|'FAILED' => {
    if (e?.status) return e.status;
    try {
      const now = new Date();
      const dateStr = e?.startTime;
      const dt = dateStr ? new Date(dateStr) : null;
      
      if (dt) {
        // If we have a valid date and it's in the future, it's UPCOMING
        if (dt > now) return 'UPCOMING';

        // Grace period: until 1 hour after the exam, if result is false, keep it UPCOMING
        const oneHourAfter = new Date(dt.getTime() + 60 * 60 * 1000);
        if (now < oneHourAfter && typeof e?.result === 'boolean' && !e.result) {
          return 'UPCOMING';
        }
      }
      
      // If it's not in the future, check if result is set
      if (typeof e?.result === 'boolean') {
        return e.result ? 'PASSED' : 'FAILED';
      }
      return 'COMPLETED';
    } catch {
      return 'COMPLETED';
    }
  };

  const byChrono = React.useMemo(() => {
    const arr = Array.isArray(exams) ? [...exams] : [];
    return arr.sort((a,b)=>{
      const ta = a?.startTime ? Date.parse(a.startTime) : 0;
      const tb = b?.startTime ? Date.parse(b.startTime) : 0;
      return tb - ta; // latest first
    });
  }, [exams]);

  const upcomingExams = byChrono.filter(e => computeStatus(e) === 'UPCOMING').reverse(); // Chronological for upcoming
  const pastExams = byChrono.filter(e => computeStatus(e) !== 'UPCOMING');

  const studentLabel = (e:any) => {
    const sid = e?.student?.id ?? e?.studentId ?? null;
    if (sid && studentsMap[String(sid)]) return studentsMap[String(sid)];
    if (sid) return `Student ${sid}`;
    return '';
  };

  return (
    <PageWrapper>
      <Navigation />
      <ContentWrapper>
        <AnimatePresence mode="wait">
          <motion.div
            key="exams-content"
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            <ContentCard variants={itemVariants}>
              <Header>
                <div className="title-group">
                  <AssignmentIcon />
                  <h3>{t('portal.exams.management', 'Exams Management')}</h3>
                </div>
                <ActionButtons>
                  <PrimaryButton whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={load} variant="secondary" style={{ background: '#f1f3f4', color: '#444' }}>
                    <RefreshIcon fontSize="small" /> {t('portal.exams.refresh','Refresh')}
                  </PrimaryButton>
                  <PrimaryButton whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={()=> setShowCreate(true)} variant="success">
                    <AddIcon fontSize="small" /> {t('portal.exams.create','Schedule Exam')}
                  </PrimaryButton>
                </ActionButtons>
                <div className="subtitle" style={{ width: '100%' }}>{t('portal.exams.subtitle', 'Manage and track student examinations')}</div>
              </Header>

              {loading && exams.length === 0 ? (
                <div style={{ textAlign:'center', padding:'3rem' }}>
                  <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: 'linear' }} style={{ display: 'inline-block' }}>
                    <RefreshIcon fontSize="large" style={{ color: 'var(--primary-blue)' }} />
                  </motion.div>
                  <p style={{ marginTop: '1rem', color: '#666' }}>{t('portal.common.loading','Loading exams…')}</p>
                </div>
              ) : error ? (
                <div style={{ textAlign:'center', padding:'2rem', color:'#fc4a1a', fontWeight:700 }}>
                  <CancelIcon fontSize="large" />
                  <p style={{ marginTop: '1rem' }}>⚠️ {error}</p>
                </div>
              ) : (
                <>
                  {upcomingExams.length > 0 && (
                    <>
                      <SectionTitle><EventIcon /> {t('portal.exams.upcoming', 'Upcoming Exams')}</SectionTitle>
                      <ExamList>
                        {upcomingExams.map((e) => (
                          <ExamItemContainer
                            key={e.id}
                            variants={itemVariants}
                            onClick={()=> navigate(`/portal/teacher/exams/${e.id}`)}
                          >
                            <ExamMainInfo>
                              <div className="icon-wrapper">
                                <AssignmentIcon />
                              </div>
                              <div className="text-content">
                                <div className="exam-title">{formatDate(e.startTime)}</div>
                                <div className="exam-meta">
                                  <div className="meta-item"><PersonIcon sx={{ fontSize: 14 }} /> {studentLabel(e)}</div>
                                  {e.location && <div className="meta-item"><LocationOnIcon sx={{ fontSize: 14 }} /> {e.location}</div>}
                                  {e.licenseType && <div className="meta-item"><BadgeIcon sx={{ fontSize: 14 }} /> {e.licenseType}</div>}
                                </div>
                              </div>
                            </ExamMainInfo>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                              <StatusChip status="UPCOMING">
                                {(() => {
                                  const now = new Date();
                                  const dt = e.startTime ? new Date(e.startTime) : null;
                                  if (dt && dt > now) return '—';
                                  return t('portal.exams.status.upcoming', 'Upcoming');
                                })()}
                              </StatusChip>
                              <ChevronRightIcon style={{ color: '#ccc' }} />
                            </div>
                          </ExamItemContainer>
                        ))}
                      </ExamList>
                    </>
                  )}

                  <SectionTitle><HistoryIcon /> {t('portal.exams.history_title', 'Exam History')}</SectionTitle>
                  {pastExams.length === 0 ? (
                    <div style={{ textAlign:'center', padding:'3rem', color:'#888', background: 'rgba(0,0,0,0.02)', borderRadius: 16 }}>
                      <HistoryIcon sx={{ fontSize: 48, opacity: 0.2, marginBottom: '1rem' }} />
                      <p>{t('portal.exams.none_past','No past exams recorded.')}</p>
                    </div>
                  ) : (
                    <ExamList>
                      {pastExams.map((e) => {
                        const status = computeStatus(e);
                        return (
                          <ExamItemContainer
                            key={e.id}
                            variants={itemVariants}
                            onClick={()=> navigate(`/portal/teacher/exams/${e.id}`)}
                          >
                            <ExamMainInfo>
                              <div className="icon-wrapper" style={{ background: 'rgba(0,0,0,0.05)', color: '#666' }}>
                                {status === 'PASSED' ? <CheckCircleIcon style={{ color: '#11998e' }} /> : status === 'FAILED' ? <CancelIcon style={{ color: '#fc4a1a' }} /> : <HistoryIcon />}
                              </div>
                              <div className="text-content">
                                <div className="exam-title">{formatDate(e.startTime)}</div>
                                <div className="exam-meta">
                                  <div className="meta-item"><PersonIcon sx={{ fontSize: 14 }} /> {studentLabel(e)}</div>
                                  {e.location && <div className="meta-item"><LocationOnIcon sx={{ fontSize: 14 }} /> {e.location}</div>}
                                  {e.licenseType && <div className="meta-item"><BadgeIcon sx={{ fontSize: 14 }} /> {e.licenseType}</div>}
                                </div>
                              </div>
                            </ExamMainInfo>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                              <StatusChip status={status}>
                                {(() => {
                                  const now = new Date();
                                  const dt = e.startTime ? new Date(e.startTime) : null;
                                  if (dt && dt > now) return '—';
                                  return t(`portal.exams.status.${status.toLowerCase()}`, status);
                                })()}
                              </StatusChip>
                              <ChevronRightIcon style={{ color: '#ccc' }} />
                            </div>
                          </ExamItemContainer>
                        );
                      })}
                    </ExamList>
                  )}
                </>
              )}
            </ContentCard>
          </motion.div>
        </AnimatePresence>
        <CreateExamModal 
          open={showCreate} 
          onClose={()=> setShowCreate(false)} 
          onCreated={load} 
          students={studentsList}
        />
      </ContentWrapper>
    </PageWrapper>
  );
};

export default TeacherExams;
